import { useEffect, useState } from "react";
import { Link, useParams } from "wouter";
import { AlertCircle, ArrowLeft, FileSearch, Loader2 } from "lucide-react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type EvidenceResponse = {
  kind: "email" | "audit";
  record: Record<string, unknown>;
};

function value(value: unknown): string {
  return value == null || value === "" ? "Not recorded" : String(value);
}

export default function ProformaEvidencePage() {
  const { id, kind, recordId } = useParams<{ id: string; kind: string; recordId: string }>();
  const [data, setData] = useState<EvidenceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE}/api/cash-receipts/proforma-payments/${id}/evidence/${kind}/${recordId}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || "Failed to load recovery evidence");
        setData(body);
      })
      .catch((reason) => {
        if (reason.name !== "AbortError") setError(reason.message || "Failed to load recovery evidence");
      });
    return () => controller.abort();
  }, [id, kind, recordId]);

  const record = data?.record;
  const bodyHtml = typeof record?.bodyHtml === "string" ? record.bodyHtml : null;
  const bodyText = typeof record?.bodyText === "string" ? record.bodyText : null;

  return (
    <Layout>
      <div className="mx-auto max-w-4xl p-6">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link href="/cash-receipts/proforma-payments">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to proforma payments
          </Link>
        </Button>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <FileSearch className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Proforma recovery evidence</h1>
              <p className="text-sm text-slate-500">Record {recordId} from this payment’s recovery investigation</p>
            </div>
          </div>

          {!data && !error && (
            <div className="flex items-center gap-2 py-10 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading evidence…
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {data?.kind === "email" && record && (
            <div className="space-y-4 text-sm">
              <dl className="grid gap-3 sm:grid-cols-2">
                <div><dt className="font-medium text-slate-500">Sent at</dt><dd>{value(record.sentAt)}</dd></div>
                <div><dt className="font-medium text-slate-500">Status</dt><dd>{record.error ? `Failed: ${value(record.error)}` : "Sent successfully"}</dd></div>
                <div><dt className="font-medium text-slate-500">Recipient</dt><dd>{value(record.toEmail)}</dd></div>
                <div><dt className="font-medium text-slate-500">Original recipient</dt><dd>{value(record.originalTo)}</dd></div>
                <div className="sm:col-span-2"><dt className="font-medium text-slate-500">Subject</dt><dd>{value(record.subject)}</dd></div>
              </dl>
              {bodyHtml ? (
                <iframe
                  title="Email body"
                  sandbox=""
                  srcDoc={`<!doctype html><html><body style="margin:0;padding:16px;background:white;font-family:sans-serif;">${bodyHtml}</body></html>`}
                  className="min-h-[320px] w-full rounded border border-slate-200 bg-white"
                />
              ) : (
                <pre className="whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-4 font-sans">{bodyText || "No email body recorded."}</pre>
              )}
            </div>
          )}

          {data?.kind === "audit" && record && (
            <div className="space-y-4 text-sm">
              <dl className="grid gap-3 sm:grid-cols-2">
                <div><dt className="font-medium text-slate-500">Recorded at</dt><dd>{value(record.createdAt)}</dd></div>
                <div><dt className="font-medium text-slate-500">Operator</dt><dd>{value(record.actorEmail)}</dd></div>
                <div className="sm:col-span-2"><dt className="font-medium text-slate-500">Action</dt><dd>{value(record.action)}</dd></div>
              </dl>
              <div>
                <div className="mb-1 font-medium text-slate-500">Recovery details</div>
                <pre className="overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-4 text-xs">
                  {JSON.stringify(record.metadata ?? {}, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}